// src/Pages/AdminCategories.js
import React, { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import axiosInstance from "../utils/axiosInstance";

export default function AdminCategories() {
  const navigate = useNavigate();

  // list
  const [allCategories, setAllCategories] = useState([]);
  const [loading, setLoading] = useState(false);

  // search
  const [search, setSearch] = useState("");

  // drawer view
  const [drawerOpen, setDrawerOpen] = useState(false);
  const [selected, setSelected] = useState(null);
  const [viewLoading, setViewLoading] = useState(false);

  // add/edit modal
  const [modalOpen, setModalOpen] = useState(false);
  const [mode, setMode] = useState("ADD"); // ADD | EDIT
  const [saving, setSaving] = useState(false);
  const [form, setForm] = useState(getEmptyForm());
  const [file, setFile] = useState(null);

  // delete
  const [deleteOpen, setDeleteOpen] = useState(false);
  const [deleteRow, setDeleteRow] = useState(null);
  const [deleteLoading, setDeleteLoading] = useState(false);

  // preview
  const previewUrl = useMemo(() => (file ? URL.createObjectURL(file) : ""), [file]);
  useEffect(() => {
    return () => {
      if (previewUrl) URL.revokeObjectURL(previewUrl);
    };
  }, [previewUrl]);

  // auth + initial load
  useEffect(() => {
    const token = localStorage.getItem("token");
    const user = JSON.parse(localStorage.getItem("user") || "null");
    const role = (user?.role || "").toUpperCase();

    if (!token || !(role === "ADMIN" || role === "STAFF")) {
      navigate("/login", { replace: true });
      return;
    }
    fetchCategories();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  /* ================== API ================== */
  const fetchCategories = async () => {
    try {
      setLoading(true);
      const res = await axiosInstance.get("/api/categories");
      const list = Array.isArray(res.data) ? res.data : [];
      setAllCategories(list);
    } catch (err) {
      console.log("GET /api/categories error", err);
      if (err?.response?.status === 401 || err?.response?.status === 403) {
        navigate("/admin", { replace: true });
      } else {
        alert(err?.response?.data?.msg || "Failed to load categories");
      }
    } finally {
      setLoading(false);
    }
  };

  /* ================== HELPERS ================== */
  // ✅ same logic as your AdminProducts (base url from axiosInstance)
  const imgUrl = (path) => {
    if (!path) return "";
    const base = (axiosInstance.defaults.baseURL || "").replace(/\/$/, "");
    if (path.startsWith("http")) return path;
    if (path.startsWith("/")) return `${base}${path}`;
    return `${base}/${path}`;
  };

  // numeric safe
  const toInt = (v, fb = 0) => {
    const n = parseInt(String(v ?? "").trim(), 10);
    return Number.isFinite(n) ? n : fb;
  };

  const categories = useMemo(() => {
    const q = search.trim().toLowerCase();

    let list = allCategories;

    if (q) {
      list = list.filter((c) => (c.name || "").toLowerCase().includes(q));
    }

    // ✅ Sort by sortOrder ASC then name ASC then id ASC
    return [...list].sort((a, b) => {
      const ao = toInt(a?.sortOrder, 0);
      const bo = toInt(b?.sortOrder, 0);
      if (ao !== bo) return ao - bo;

      const an = String(a?.name || "").toLowerCase();
      const bn = String(b?.name || "").toLowerCase();
      if (an < bn) return -1;
      if (an > bn) return 1;

      return toInt(a?.id, 0) - toInt(b?.id, 0);
    });
  }, [allCategories, search]);

  const total = categories.length;

  const openView = async (row) => {
    try {
      setDrawerOpen(true);
      setViewLoading(true);
      setSelected(null);

      // if you don't have GET /api/categories/:id, this still works (fallback)
      const res = await axiosInstance.get(`/api/categories/${row.id}`);
      setSelected(res.data);
    } catch (err) {
      console.log("GET /api/categories/:id error (fallback to row)", err);
      setSelected(row);
    } finally {
      setViewLoading(false);
    }
  };

  const closeView = () => {
    setDrawerOpen(false);
    setSelected(null);
  };

  const openAdd = () => {
    setMode("ADD");
    setForm(getEmptyForm());
    setFile(null);
    setSelected(null);
    setModalOpen(true);
  };

  const openEdit = async (row) => {
    try {
      // try full data (optional)
      const res = await axiosInstance.get(`/api/categories/${row.id}`);
      const c = res.data || row;

      setMode("EDIT");
      setSelected(c);
      setForm({
        name: c?.name || "",
        sortOrder: String(toInt(c?.sortOrder, 0)),
      });
      setFile(null);
      setModalOpen(true);
    } catch (err) {
      console.log("openEdit error", err);
      // fallback
      setMode("EDIT");
      setSelected(row);
      setForm({
        name: row?.name || "",
        sortOrder: String(toInt(row?.sortOrder, 0)),
      });
      setFile(null);
      setModalOpen(true);
    }
  };

  const closeModal = () => {
    setModalOpen(false);
    setSaving(false);
    setFile(null);
  };

  const saveCategory = async () => {
    try {
      if (!form.name.trim()) return alert("Category name required");

      // For ADD: image required
      if (mode === "ADD" && !file) return alert("Category image required");

      const sortOrderInt = toInt(form.sortOrder, 0);

      setSaving(true);

      const fd = new FormData();
      fd.append("name", form.name.trim());
      fd.append("sortOrder", String(sortOrderInt)); // ✅ send sortOrder
      if (file) fd.append("image", file);

      if (mode === "ADD") {
        await axiosInstance.post("/api/categories", fd, {
          headers: { "Content-Type": "multipart/form-data" },
        });
      } else {
        await axiosInstance.put(`/api/categories/${selected.id}`, fd, {
          headers: { "Content-Type": "multipart/form-data" },
        });
      }

      closeModal();
      fetchCategories();
    } catch (err) {
      console.log("saveCategory error", err);
      alert(err?.response?.data?.msg || "Save failed");
    } finally {
      setSaving(false);
    }
  };

  const openDelete = (row) => {
    setDeleteRow(row);
    setDeleteOpen(true);
  };

  const closeDelete = () => {
    setDeleteOpen(false);
    setDeleteRow(null);
  };

  const confirmDelete = async () => {
    if (!deleteRow?.id) return;
    try {
      setDeleteLoading(true);
      await axiosInstance.delete(`/api/categories/${deleteRow.id}`);
      closeDelete();
      fetchCategories();
    } catch (err) {
      console.log("DELETE /api/categories/:id error", err);
      alert(err?.response?.data?.msg || "Delete failed");
    } finally {
      setDeleteLoading(false);
    }
  };

  return (
    <div style={styles.page}>
      {/* Topbar */}
      <div style={styles.topbar}>
        <h2 style={styles.title}>Categories</h2>
        <div style={{ display: "flex", gap: 10 }}>
          <button onClick={() => navigate("/admin/dashboard")} style={styles.btn}>
            ← Dashboard
          </button>
          <button onClick={openAdd} style={styles.primaryBtn}>
            + Add Category
          </button>
        </div>
      </div>

      {/* Search */}
      <div style={styles.searchBar}>
        <div style={styles.searchIcon}>🔍</div>
        <input
          style={styles.searchInput}
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          placeholder="Search category name..."
        />
        {search.trim() ? (
          <button type="button" style={styles.clearBtn} onClick={() => setSearch("")} title="Clear">
            ✕
          </button>
        ) : null}
      </div>

      {/* Card + Table */}
      <div style={styles.card}>
        {loading ? (
          <div style={styles.info}>Loading...</div>
        ) : (
          <>
            <div style={styles.meta}>
              Total Categories: <b>{total}</b> (Sorted by <b>Sort Order</b>)
            </div>

            <div style={{ overflowX: "auto" }}>
              <table style={styles.table}>
                <thead>
                  <tr>
                    {["ID", "Sort Order", "Category", "Image", "Created", "Actions"].map((h) => (
                      <th key={h} style={styles.th}>
                        {h}
                      </th>
                    ))}
                  </tr>
                </thead>

                <tbody>
                  {categories.length === 0 ? (
                    <tr>
                      <td colSpan={6} style={styles.empty}>
                        No categories found
                      </td>
                    </tr>
                  ) : (
                    categories.map((c) => (
                      <tr key={c.id}>
                        <td style={styles.td}>{c.id}</td>

                        <td style={styles.td}>
                          <span style={styles.badge}>{toInt(c.sortOrder, 0)}</span>
                        </td>

                        <td style={styles.td}>
                          <div style={{ fontWeight: 800 }}>{c.name}</div>
                        </td>

                        <td style={styles.td}>
                          {c.image ? (
                            <img
                              src={imgUrl(c.image)}
                              alt={c.name}
                              style={styles.thumb}
                              onError={(e) => {
                                e.currentTarget.style.display = "none";
                              }}
                            />
                          ) : (
                            <div style={styles.thumbPlaceholder}>No Img</div>
                          )}
                        </td>

                        <td style={styles.td}>
                          {c.createdAt ? new Date(c.createdAt).toLocaleString() : "-"}
                        </td>

                        <td style={styles.td}>
                          <div style={styles.actions}>
                            <button style={styles.actionBtn} onClick={() => openView(c)}>
                              View
                            </button>
                            <button style={styles.actionBtn} onClick={() => openEdit(c)}>
                              Edit
                            </button>
                            <button style={styles.dangerBtn} onClick={() => openDelete(c)}>
                              Delete
                            </button>
                          </div>
                        </td>
                      </tr>
                    ))
                  )}
                </tbody>
              </table>
            </div>
          </>
        )}
      </div>

      {/* DRAWER VIEW */}
      {drawerOpen && (
        <div style={styles.drawerOverlay} onClick={closeView}>
          <div style={styles.drawer} onClick={(e) => e.stopPropagation()}>
            <div style={styles.drawerHeader}>
              <h3 style={{ margin: 0 }}>Category Details</h3>
              <button style={styles.btnSmall} onClick={closeView}>
                ✕
              </button>
            </div>

            {viewLoading ? (
              <div style={styles.info}>Loading...</div>
            ) : selected ? (
              <div style={styles.drawerBody}>
                <div style={{ display: "flex", gap: 12, alignItems: "center", marginBottom: 14 }}>
                  {selected.image ? (
                    <img
                      src={imgUrl(selected.image)}
                      alt="cat"
                      style={{
                        width: 88,
                        height: 88,
                        borderRadius: 14,
                        objectFit: "cover",
                        border: "1px solid #eee",
                      }}
                    />
                  ) : (
                    <div style={{ ...styles.thumbPlaceholder, width: 88, height: 88, borderRadius: 14 }}>
                      No Img
                    </div>
                  )}
                  <div>
                    <div style={{ fontSize: 18, fontWeight: 900 }}>{selected.name}</div>
                    <div style={{ fontSize: 12, color: "#666", marginTop: 4 }}>
                      ID: <b>{selected.id}</b>
                      {"  "}•{"  "}
                      Order: <b>{toInt(selected.sortOrder, 0)}</b>
                    </div>
                  </div>
                </div>

                <Row label="ID" value={selected.id} />
                <Row label="Name" value={selected.name} />
                <Row label="Sort Order" value={toInt(selected.sortOrder, 0)} />
                <Row label="Image" value={selected.image || "-"} />
                <Row label="Created" value={selected.createdAt ? new Date(selected.createdAt).toLocaleString() : "-"} />
                <Row label="Updated" value={selected.updatedAt ? new Date(selected.updatedAt).toLocaleString() : "-"} />

                <div style={{ display: "flex", gap: 10, marginTop: 14 }}>
                  <button style={styles.actionBtn} onClick={() => openEdit(selected)}>
                    Edit
                  </button>
                  <button style={styles.dangerBtn} onClick={() => openDelete(selected)}>
                    Delete
                  </button>
                </div>
              </div>
            ) : (
              <div style={styles.info}>No data</div>
            )}
          </div>
        </div>
      )}

      {/* ADD / EDIT MODAL */}
      {modalOpen && (
        <div style={styles.modalOverlay} onClick={closeModal}>
          <div style={styles.modal} onClick={(e) => e.stopPropagation()}>
            <div style={styles.modalHeader}>
              <h3 style={{ margin: 0 }}>{mode === "ADD" ? "Add Category" : "Edit Category"}</h3>
              <button style={styles.btnSmall} onClick={closeModal}>
                ✕
              </button>
            </div>

            <div style={styles.modalBody}>
              <Field label="Category Name *">
                <input
                  style={styles.modalInput}
                  value={form.name}
                  onChange={(e) => setForm({ ...form, name: e.target.value })}
                  placeholder="medicine"
                />
              </Field>

              <div style={{ height: 12 }} />

              <Field label="Sort Order (Number) *">
                <input
                  type="number"
                  style={styles.modalInput}
                  value={form.sortOrder}
                  onChange={(e) => setForm({ ...form, sortOrder: e.target.value })}
                  placeholder="0"
                />
                <div style={{ marginTop: 6, fontSize: 12, color: "#666" }}>
                  Smaller number shows first. Example: 0, 1, 2...
                </div>
              </Field>

              <div style={{ height: 12 }} />

              <Field label={mode === "ADD" ? "Image *" : "Image (optional - upload to replace)"}>
                <input
                  type="file"
                  accept="image/*"
                  onChange={(e) => {
                    const f = (e.target.files || [])[0] || null;
                    setFile(f);
                  }}
                />

                {/* preview new file */}
                {file && (
                  <div style={{ marginTop: 10 }}>
                    <div style={{ fontSize: 12, color: "#666", marginBottom: 6 }}>Preview</div>
                    <img src={previewUrl} alt="preview" style={styles.previewImgLarge} />
                  </div>
                )}

                {/* show current image in edit if no new file */}
                {mode === "EDIT" && !file && selected?.image && (
                  <div style={{ marginTop: 10 }}>
                    <div style={{ fontSize: 12, color: "#666", marginBottom: 6 }}>
                      Current Image (upload new to replace)
                    </div>
                    <img src={imgUrl(selected.image)} alt="current" style={styles.previewImgLarge} />
                  </div>
                )}
              </Field>
            </div>

            <div style={styles.modalFooter}>
              <button style={styles.btn} onClick={closeModal} disabled={saving}>
                Cancel
              </button>
              <button style={styles.primaryBtn} onClick={saveCategory} disabled={saving}>
                {saving ? "Saving..." : "Save"}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* DELETE CONFIRM */}
      {deleteOpen && (
        <div style={styles.modalOverlay} onClick={closeDelete}>
          <div style={styles.confirm} onClick={(e) => e.stopPropagation()}>
            <h3 style={{ marginTop: 0 }}>Delete Category?</h3>
            <p style={{ marginTop: 6, fontSize: 14 }}>
              Are you sure you want to delete <b>{deleteRow?.name}</b>?
            </p>

            <div style={{ display: "flex", gap: 10, justifyContent: "flex-end" }}>
              <button style={styles.btn} onClick={closeDelete} disabled={deleteLoading}>
                Cancel
              </button>
              <button style={styles.dangerBtn} onClick={confirmDelete} disabled={deleteLoading}>
                {deleteLoading ? "Deleting..." : "Delete"}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

/* helpers */
function getEmptyForm() {
  return { name: "", sortOrder: "0" };
}

function Row({ label, value }) {
  return (
    <div style={styles.row}>
      <div style={styles.rowLabel}>{label}</div>
      <div style={styles.rowValue}>{value ?? "-"}</div>
    </div>
  );
}

function Field({ label, children }) {
  return (
    <div>
      <label style={styles.label}>{label}</label>
      {children}
    </div>
  );
}

/* styles */
const styles = {
  page: { padding: 22, background: "#f4f6f8", minHeight: "100vh" },
  topbar: { display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 12 },
  title: { margin: 0, fontSize: 34, fontWeight: 800, letterSpacing: -0.5 },

  btn: {
    padding: "12px 14px",
    cursor: "pointer",
    borderRadius: 10,
    border: "1px solid #cfcfcf",
    background: "#f3f3f3",
    fontSize: 14,
  },
  btnSmall: {
    padding: "8px 10px",
    cursor: "pointer",
    borderRadius: 10,
    border: "1px solid #cfcfcf",
    background: "#f3f3f3",
    fontSize: 13,
  },
  primaryBtn: {
    padding: "12px 16px",
    cursor: "pointer",
    background: "#111",
    color: "#fff",
    border: "none",
    borderRadius: 10,
    fontSize: 14,
  },

  badge: {
    display: "inline-block",
    padding: "6px 10px",
    borderRadius: 999,
    border: "1px solid #e7e7e7",
    background: "#fafafa",
    fontWeight: 800,
    fontSize: 12,
    color: "#111",
    minWidth: 44,
    textAlign: "center",
  },

  // search
  searchBar: {
    display: "flex",
    alignItems: "center",
    gap: 10,
    background: "#fff",
    border: "1px solid #e6e6e6",
    borderRadius: 16,
    padding: "12px 14px",
    boxShadow: "0 8px 22px rgba(0,0,0,0.06)",
    marginBottom: 12,
    maxWidth: 520,
  },
  searchIcon: { fontSize: 16, opacity: 0.75 },
  searchInput: { border: "none", outline: "none", fontSize: 15, flex: 1, background: "transparent" },
  clearBtn: {
    border: "none",
    background: "#f2f2f2",
    borderRadius: 10,
    cursor: "pointer",
    padding: "8px 10px",
    fontSize: 14,
  },

  card: { background: "#fff", borderRadius: 16, padding: 16, boxShadow: "0 10px 26px rgba(0,0,0,0.10)" },
  info: { padding: 10, fontSize: 14 },
  meta: { marginBottom: 10, color: "#222", fontSize: 16 },

  table: { width: "100%", borderCollapse: "collapse", minWidth: 980 },
  th: { textAlign: "left", padding: "12px 8px", borderBottom: "1px solid #eee", fontSize: 14, color: "#333", whiteSpace: "nowrap" },
  td: { padding: "14px 8px", borderBottom: "1px solid #f0f0f0", fontSize: 14, verticalAlign: "middle" },
  empty: { padding: 16, textAlign: "center", color: "#666", fontSize: 14 },

  thumb: { width: 56, height: 56, borderRadius: 14, objectFit: "cover", border: "1px solid #eee", background: "#fff" },
  thumbPlaceholder: {
    width: 56,
    height: 56,
    borderRadius: 14,
    border: "1px solid #eee",
    background: "#f3f3f3",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    color: "#888",
    fontSize: 11,
  },

  actions: { display: "flex", gap: 10, flexWrap: "wrap" },
  actionBtn: { padding: "9px 12px", cursor: "pointer", borderRadius: 10, border: "1px solid #ddd", background: "#fff", fontSize: 13 },
  dangerBtn: { padding: "9px 12px", cursor: "pointer", borderRadius: 10, border: "1px solid #ffcccc", background: "#ffecec", fontSize: 13 },

  // drawer
  drawerOverlay: { position: "fixed", inset: 0, background: "rgba(0,0,0,0.25)", display: "flex", justifyContent: "flex-end", zIndex: 50 },
  drawer: { width: 420, maxWidth: "92vw", height: "100%", background: "#fff", padding: 14, boxShadow: "-10px 0 24px rgba(0,0,0,0.14)" },
  drawerHeader: { display: "flex", justifyContent: "space-between", alignItems: "center", paddingBottom: 10, borderBottom: "1px solid #eee" },
  drawerBody: { paddingTop: 12 },

  row: { display: "grid", gridTemplateColumns: "150px 1fr", gap: 10, padding: "10px 0", borderBottom: "1px dashed #f0f0f0" },
  rowLabel: { color: "#666", fontSize: 12 },
  rowValue: { color: "#111", fontWeight: 700, fontSize: 13 },

  // modal
  modalOverlay: { position: "fixed", inset: 0, background: "rgba(0,0,0,0.25)", display: "flex", alignItems: "center", justifyContent: "center", zIndex: 60, padding: 16 },
  modal: { width: 560, maxWidth: "96vw", background: "#fff", borderRadius: 16, boxShadow: "0 14px 32px rgba(0,0,0,0.18)", display: "flex", flexDirection: "column" },
  modalHeader: { display: "flex", justifyContent: "space-between", alignItems: "center", padding: 14, borderBottom: "1px solid #eee" },
  modalBody: { padding: 14, maxHeight: "70vh", overflowY: "auto" },
  modalFooter: { padding: 14, borderTop: "1px solid #eee", display: "flex", justifyContent: "flex-end", gap: 10, background: "#fff" },

  label: { display: "block", fontSize: 12, color: "#666", marginBottom: 6 },
  modalInput: { width: "100%", padding: "10px 12px", borderRadius: 10, border: "1px solid #ddd", outline: "none", fontSize: 14 },

  previewImgLarge: { width: 160, height: 160, borderRadius: 16, objectFit: "cover", border: "1px solid #eee" },

  // confirm
  confirm: { width: 420, maxWidth: "95vw", background: "#fff", borderRadius: 14, padding: 16, boxShadow: "0 14px 32px rgba(0,0,0,0.18)" },
};